import { type ActionsObject, makeAction, makeReducer } from '@finn-no/redux-actions';
import { type AccommodationState, ApiActionStatus, type BasicHotel, type Filters, type MapState, type ThunkResult } from '@/interfaces';
import type { Dispatch } from 'redux';
import { getHotelsBasic } from '@/utils/fetch/kayak/kayakFetcher';
import { defaultBasicHotelsResponse } from './defaultState';
import { filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';

const MAX_FETCH_ATTEMPTS = 5;
const MAX_PAGE_COUNT = 8;
const MAP_PAGE_SIZE = 250;

// biome-ignore lint/suspicious/noExplicitAny: This existed before biome was added
const convertKeyToHotelKey = (hotels: any[]): BasicHotel[] => (hotels ? hotels.map((hotel) => ({ ...hotel, hotelKey: hotel.key })) : []);

export const filtersToCurrentFilters = (filters: Filters): string =>
    filtersToResultPageBrowserQueryParams({ ...filters, page: undefined, sort: undefined }, false);

export const actions: ActionsObject<MapState> = {};

actions.setMapState = makeAction(
    'MAP/SET_STATE',
    (state: MapState, action): MapState => ({
        ...state,
        ...action.payload,
    }),
);

actions.resetMapHotels = makeAction(
    'MAP/RESET_HOTELS',
    (state: MapState, action): MapState => ({
        ...state,
        currentFilters: action.payload,
        hotels: [{ ...defaultBasicHotelsResponse }],
    }),
);

actions.setMapHotelsPending = makeAction('MAP/HOTELS_PENDING', (state: MapState, action): MapState => {
    const hotels = [...state.hotels];
    hotels[action.payload] = {
        ...hotels[action.payload],
        status: ApiActionStatus.PENDING,
        attempts: hotels[action.payload].attempts + 1,
    };
    return {
        ...state,
        hotels,
    };
});

actions.setMapHotels = makeAction('MAP/HOTELS_OK', (state: MapState, action): MapState => {
    if (action.payload.queryParams !== state.currentFilters) {
        return state;
    }
    let hotels = [...state.hotels];
    const isComplete = action.payload.data.isComplete || hotels[action.payload.pageNumber].attempts >= MAX_FETCH_ATTEMPTS;
    if (action.payload.pageNumber === 0 && isComplete) {
        const maxHotelCount = action.payload.data.totalFilteredResults;
        const mapPages = maxHotelCount ? Math.min(Math.ceil(maxHotelCount / MAP_PAGE_SIZE), MAX_PAGE_COUNT) : 0;
        if (mapPages > 1) {
            hotels = [...hotels, ...Array(mapPages - hotels.length).fill({ ...defaultBasicHotelsResponse })];
        }
    }
    hotels[action.payload.pageNumber] = {
        ...hotels[action.payload.pageNumber],
        ...action.payload.data,
        status: ApiActionStatus.OK,
        results: convertKeyToHotelKey(action.payload.data.results),
        isComplete,
    };
    return {
        ...state,
        hotels,
    };
});

actions.setMapHotelsError = makeAction('MAP/HOTELS_ERROR', (state: MapState, action): MapState => {
    if (action.payload.queryParams !== state.currentFilters) {
        return state;
    }
    const hotels = [...state.hotels];
    hotels[action.payload.pageNumber] = {
        ...hotels[action.payload.pageNumber],
        status: ApiActionStatus.ERROR,
        isComplete: true,
    };
    return {
        ...state,
        hotels,
    };
});

export const pollHotelPageForMap =
    (pageNumber: number): ThunkResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => AccommodationState): Promise<void> => {
        const filters = getState().filters;
        dispatch(actions.setMapHotelsPending(pageNumber));
        const { status, data } = await getHotelsBasic({ ...filters, page: pageNumber + 1 }, MAP_PAGE_SIZE);
        const queryParams = filtersToCurrentFilters(filters);
        if (status === 200) {
            dispatch(actions.setMapHotels({ data, pageNumber, queryParams }));
        } else {
            dispatch(actions.setMapHotelsError({ pageNumber, queryParams }));
        }
    };

export const mapReducer = makeReducer<MapState>(actions);
